"use client";

import React from "react";
import { motion } from "framer-motion";
import { FileSearch, SlidersHorizontal, MapPin, Star, Code as Code2, ChartBar as BarChart3, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const processSteps = [{
  stepNumber: "01",
  title: "In-Depth Technical & Intent Audit",
  description: "I personally audit your technical infrastructure, crawl health, indexation bottlenecks, and content positioning against real search intent.",
  deliverable: "Custom technical roadmap & prioritized fix list",
  icon: "FileSearch"
}, {
  stepNumber: "02",
  title: "On-Page & Keyword Optimization",
  description: "I restructure site hierarchy and refine on-page content, metadata, and internal linking to align with high-converting search queries.",
  deliverable: "Optimized page architecture & target keywords",
  icon: "SlidersHorizontal"
}, {
  stepNumber: "03",
  title: "Citations & Entity Building",
  description: "I build clean, consistent local and industry entity citations across high-trust databases to solidify authority signals in Google.",
  deliverable: "Verified citation profile & trust synchronization",
  icon: "MapPin"
}, {
  stepNumber: "04",
  title: "Review System & Local Signals",
  description: "I establish structured review-gathering workflows to capture authentic customer reviews and strengthen your Google Business Profile.",
  deliverable: "Review strategy & local pack positioning",
  icon: "Star"
}, {
  stepNumber: "05",
  title: "Advanced Schema & AI Search Readiness",
  description: "I craft tailored JSON-LD structured data schema so search engines and modern AI search systems understand your exact entity authority.",
  deliverable: "Comprehensive schema deployment & AI indexing",
  icon: "Code2"
}, {
  stepNumber: "06",
  title: "Transparent Reporting & Direct Iteration",
  description: "You receive concise, plain-English monthly performance reports outlining keyword movements, organic leads, and continuous strategic growth.",
  deliverable: "Monthly reporting dashboard & strategy call",
  icon: "BarChart3"
}];
const renderStepIcon = (iconName: string) => {
  const props = {
    className: "w-5 h-5 text-primary"
  };
  switch (iconName) {
    case "FileSearch":
      return <FileSearch {...props} />;
    case "SlidersHorizontal":
      return <SlidersHorizontal {...props} />;
    case "MapPin":
      return <MapPin {...props} />;
    case "Star":
      return <Star {...props} />;
    case "Code2":
      return <Code2 {...props} />;
    case "BarChart3":
      return <BarChart3 {...props} />;
    default:
      return <FileSearch {...props} />;
  }
};
export default function Methodology() {
  return <section id="methodologyprocessflow" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/10 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-10 right-10 w-[400px] h-[400px] bg-secondary/50 rounded-full blur-2xl pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24">
          <Badge variant="outline" className="mb-4 border-accent text-foreground bg-secondary/60 px-3 py-1 rounded-full font-medium">
            Structured SEO Execution
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-6">
            My Step-by-Step SEO Methodology
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            I do not rely on generic automated tools or agency templates. When
            you work with me at YE Consultancy, every stage is hands-on, tailored,
            and measured against real business outcomes.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-accent/40 -translate-y-1/2 -z-10" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {processSteps.map((step, i) => <motion.div key={step.stepNumber} data-index={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.5,
            delay: i * 0.1,
            ease: "easeOut"
          }} className="flex flex-col h-full">
                <Card className="flex-1 bg-card border-border shadow-md hover:shadow-lg transition-[border-color,box-shadow] duration-300 relative overflow-hidden rounded-xl group hover:border-primary/40">
                  {}
                  <div className="h-1.5 w-full bg-accent/30 group-hover:bg-accent transition-colors duration-300" />

                  <CardHeader className="pt-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      {}
                      <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary text-primary-foreground font-bold text-lg shadow-sm">
                        {step.stepNumber}
                      </span>

                      {}
                      <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center border border-border">
                        {renderStepIcon(step.icon)}
                      </div>
                    </div>

                    <CardTitle className="text-xl font-bold font-serif text-foreground group-hover:text-primary transition-colors duration-200">
                      {step.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col justify-between pt-0 pb-6">
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                      {step.description}
                    </p>

                    <div>
                      <Separator className="mb-4 bg-border" />
                      <div className="flex items-start gap-2 text-xs sm:text-sm text-foreground/80 font-medium">
                        <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                        <span>
                          <strong className="text-foreground">Deliverable:</strong>{" "}
                          {step.deliverable}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-16 sm:mt-20 bg-card border border-border rounded-2xl p-8 sm:p-10 text-center shadow-lg relative overflow-hidden">
          <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-accent/20 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -left-12 -top-12 w-48 h-48 bg-primary/10 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10 max-w-2xl mx-auto">
            <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground mb-4">
              You Work Directly With Me At Every Step
            </h3>
            <p className="text-muted-foreground text-base mb-8">
              No account managers or junior handlers. Get tailored SEO execution,
              transparent communication, and clear deliverables tailored for your growth.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium px-6">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium px-6 border-border hover:bg-secondary">
                <Link to="/CaseStudiesPage">Explore My Case Studies</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}